import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil } from "lucide-react";
import type { SeedSummary, SeedVisibility, UpdateSeedRequest } from "@shared/api-types.js";
import { Button } from "@web-shared/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@web-shared/components/ui/dialog";
import { Input } from "@web-shared/components/ui/input";
import { json, webApi } from "@web-shared/lib/web-api";
import { cn } from "@web-shared/lib/utils";

export const VISIBILITY: Array<{ id: SeedVisibility; label: string; hint: string }> = [
  { id: "public", label: "Public", hint: "In the feed and on your profile." },
  { id: "unlisted", label: "Unlisted", hint: "Anyone with the link — never in a feed." },
  { id: "private", label: "Private", hint: "Only you." },
];

/** The producer's edit: title, description, who can see it. */
export function EditSeedButton({ seed }: { seed: SeedSummary }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        <Pencil /> Edit
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md">
          <DialogTitle>Edit seed</DialogTitle>
          <DialogDescription className="sr-only">Title, description and visibility.</DialogDescription>
          {open && <EditSeedForm seed={seed} onDone={() => setOpen(false)} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

function EditSeedForm({ seed, onDone }: { seed: SeedSummary; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(seed.title);
  const [description, setDescription] = useState(seed.description ?? "");
  const [visibility, setVisibility] = useState<SeedVisibility>(seed.visibility ?? "public");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const changes: UpdateSeedRequest = {};
    if (title.trim() !== seed.title) changes.title = title.trim();
    if (description.trim() !== (seed.description ?? "")) changes.description = description.trim() || null;
    if (visibility !== seed.visibility) changes.visibility = visibility;
    if (Object.keys(changes).length === 0) return onDone();
    setBusy(true);
    setError(null);
    try {
      await webApi(`/seeds/${encodeURIComponent(seed.id)}`, json("PATCH", changes));
      await queryClient.invalidateQueries({ queryKey: ["web"] });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4">
      <label className="block space-y-1 text-xs text-muted-foreground">
        Title
        <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
      </label>
      <label className="block space-y-1 text-xs text-muted-foreground">
        Description
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={2000}
          rows={4}
          className="w-full resize-none rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring"
          placeholder="What it is, what you'd like someone to do with it"
        />
      </label>
      <VisibilityPicker value={visibility} onChange={setVisibility} />
      {error && <p className="text-xs text-removed">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={busy || !title.trim()}>
          {busy && <Loader2 className="animate-spin" />}
          Save
        </Button>
      </div>
    </form>
  );
}

export function VisibilityPicker({ value, onChange }: { value: SeedVisibility; onChange: (v: SeedVisibility) => void }) {
  return (
    <fieldset className="space-y-1.5">
      <legend className="mb-1 text-xs text-muted-foreground">Who can see it</legend>
      {VISIBILITY.map((v) => (
        <label
          key={v.id}
          className={cn(
            "flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm",
            value === v.id ? "border-primary bg-primary/10" : "border-border",
          )}
        >
          <input type="radio" name="visibility" checked={value === v.id} onChange={() => onChange(v.id)} className="mt-1 accent-[var(--primary)]" />
          <span>
            {v.label}
            <span className="block text-xs text-muted-foreground">{v.hint}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}
